/**
 * @documental
 * @export Field
 * @name
 * RadioGroup.Field
 */

import { useCallback, FC, ReactNode, useMemo, useEffect } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { FieldBaseProps } from "../FieldBaseProps";
import { Input } from "./Input";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { NoPaddingFormComponents } from "../shared";
import { VALIDATION } from "../../constants/validation";

/**
 * The `Field` component properties.
 */
export interface FieldProps extends FieldBaseProps<string | number | boolean | null>, NoPaddingFormComponents {
  /**
   * The options to display.
   */
  readonly children?: ReactNode;

  /**
   * Display options in a horizontal layout.
   * Value can be "vertical" or "horizontal".
   * Defaults to "vertical" if no value is supplied.
   */
  readonly orientation?: "vertical" | "horizontal";
}

/**
 * A React Hook Form compatible RadioGroup component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, children, defaultValue = null, required = false, validator } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback(
    (value: string | number | null, formValues: FieldValues) => {
      if (required && value === null) return VALIDATION.REQUIRED;

      return validator ? validator(value, formValues) : true;
    },
    [required, validator],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [validateHandler, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, onChange, onBlur, value } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input {...props} ref={ref} value={value} onChange={onChange} onFocusExit={onBlur}>
          {children}
        </Input>
      </ValidityGroup>
    </SubmitGroup>
  );
};
